import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { Subscription } from 'rxjs';

import { TranslatePipe } from 'app/core/i18n/translate.pipe';

import { DocumentGenerateService } from '../../document-generate.service';
import { ShipToSearchDialogData } from '../../interfaces/dialog-data.interface';
import { LocationTwoService } from '../../../../locations/location-two/location-two.service';
import { RouteService } from '../../../../routing/route/route.service';

@Component({
  selector: 'app-ship-to-search-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    TranslatePipe,
  ],
  templateUrl: './ship-to-search-modal.component.html',
})
export class ShipToSearchModalComponent implements OnInit, OnDestroy {
  @ViewChild('resultsScroll') resultsScroll?: ElementRef<HTMLElement>;
  @ViewChild('cardsScroll') cardsScroll?: ElementRef<HTMLElement>;

  search = '';
  results: any[] = [];
  selectedId: string | null = null;

  readonly pageSize = 10;
  page = 1;
  total = 0;
  loading = false;

  salesOffices: any[] = [];
  allDepartments: string[] = [];
  allRoutes: any[] = [];

  officeSearch = '';
  officeFilter = '';
  departmentSearch = '';
  departmentFilter = '';
  routeSearch = '';
  routeFilter: number | null = null;

  // en pantallas de poca altura (móvil horizontal) los filtros no dejan ver la tabla
  shortScreen = false;
  showFilters = false;

  private subs: Subscription[] = [];

  constructor(
    private dialogRef: MatDialogRef<ShipToSearchModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ShipToSearchDialogData,
    private service: DocumentGenerateService,
    private locationTwoService: LocationTwoService,
    private routeService: RouteService,
    private breakpointObserver: BreakpointObserver
  ) {}

  ngOnInit(): void {
    this.search = this.data?.initialSearch ?? '';
    this.salesOffices = this.data?.salesOffices ?? [];
    this.loadDepartments();
    this.loadRoutes();
    this.subs.push(
      this.breakpointObserver.observe('(max-height: 560px)').subscribe((state) => {
        this.shortScreen = state.matches;
      })
    );
    this.load();
  }

  get activeFilterCount(): number {
    return [this.officeFilter, this.departmentFilter, this.routeFilter].filter(
      (f) => f !== '' && f !== null && f !== undefined
    ).length;
  }

  // todos los departamentos activos del sistema
  private loadDepartments(): void {
    this.locationTwoService.findToComboBox().subscribe({
      next: (res) => {
        this.allDepartments = (res?.data ?? [])
          .map((d: any) => d?.name)
          .filter(Boolean)
          .sort();
      },
      error: () => {
        this.allDepartments = [];
      },
    });
  }

  // todas las rutas (territorios) activas del sistema, acotadas a la company del documento
  private loadRoutes(): void {
    this.routeService.findToComboBox(this.data?.companyMongoId).subscribe({
      next: (res) => {
        this.allRoutes = res?.data ?? [];
      },
      error: () => {
        this.allRoutes = [];
      },
    });
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  onSearchEnter(): void {
    this.load();
  }

  private load(): void {
    this.page = 1;
    this.fetch();
  }

  private fetch(): void {
    this.selectedId = null;
    this.loading = true;
    this.service
      .findShipTosWithoutOffice(
        this.search,
        this.officeFilter,
        this.departmentFilter,
        this.routeFilter ?? undefined,
        this.page,
        this.pageSize,
        this.data?.companyIds
      )
      .subscribe({
        next: (res) => {
          this.results = res?.data?.data ?? [];
          this.total = res?.data?.pageInfo?.total ?? 0;
          this.loading = false;
        },
        error: () => {
          this.results = [];
          this.total = 0;
          this.loading = false;
        },
      });
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  changePage(delta: number): void {
    const next = this.page + delta;
    if (next < 1 || next > this.totalPages || this.loading) return;
    this.page = next;
    this.resultsScroll?.nativeElement.scrollTo({ top: 0 });
    this.cardsScroll?.nativeElement.scrollTo({ top: 0 });
    this.fetch();
  }

  code(s: any): string {
    return s?.externalKeys?.code || s?.code || '';
  }

  department(s: any): string {
    return s?.department ?? '-';
  }

  route(s: any): string {
    return (s?.routes ?? []).map((r: any) => r?.name).filter(Boolean).join(', ') || '-';
  }

  office(s: any): string {
    return s?.routingSalesOffice?.name ?? '-';
  }

  // filtro Oficina (escribible)
  get filteredOffices(): any[] {
    const t = (this.officeSearch || '').toLowerCase();
    return this.salesOffices.filter((o) => (o.name || '').toLowerCase().includes(t));
  }

  onOfficeInput(): void {
    if (!this.officeSearch) {
      this.officeFilter = '';
      this.load();
    }
  }

  onOfficeSelected(name: string): void {
    const office = this.salesOffices.find((o) => o.name === name);
    this.officeFilter = office?._id?.toString() ?? '';
    this.officeSearch = office?.name ?? '';
    this.load();
  }

  // filtro Departamento (escribible)
  get filteredDepartments(): string[] {
    const t = (this.departmentSearch || '').toLowerCase();
    return this.allDepartments.filter((d) => d.toLowerCase().includes(t));
  }

  onDepartmentInput(): void {
    if (!this.departmentSearch) {
      this.departmentFilter = '';
      this.load();
    }
  }

  onDepartmentSelected(department: string): void {
    this.departmentFilter = department;
    this.departmentSearch = department;
    this.load();
  }

  // filtro Ruta (escribible)
  get filteredRoutes(): any[] {
    const t = (this.routeSearch || '').toLowerCase();
    return this.allRoutes.filter((r) => (r.name || '').toLowerCase().includes(t));
  }

  onRouteInput(): void {
    if (!this.routeSearch) {
      this.routeFilter = null;
      this.load();
    }
  }

  onRouteSelected(name: string): void {
    const route = this.allRoutes.find((r) => r.name === name);
    this.routeFilter = route?.id ?? null;
    this.routeSearch = route?.name ?? '';
    this.load();
  }

  select(s: any): void {
    this.selectedId = s?._id?.toString() ?? null;
  }

  isSelected(s: any): boolean {
    return this.selectedId === s?._id?.toString();
  }

  confirm(): void {
    const found = this.results.find((s) => s._id?.toString() === this.selectedId);
    this.dialogRef.close(found);
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
